import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import cn from '@/utils/cn';

/**
 * Off-canvas panel used for mobile navigation.
 *
 * Built on Radix Dialog rather than hand-rolled: an open drawer over content
 * must trap focus, close on Escape and on backdrop click, restore focus to the
 * trigger, and mark the background inert. Radix already does all of that
 * correctly, and `@radix-ui/react-dialog` is already a dependency.
 */
export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SheetPrimitive.Portal>
    <SheetPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      )}
    />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        // 272px, clamped so a visible strip of content always shows behind the
        // scrim - the standard drawer measure on a 360px phone.
        'fixed inset-y-0 left-0 z-50 flex h-full w-[17rem] max-w-[85vw] flex-col',
        'border-r border-sidebar-border bg-sidebar shadow-lg',
        'pt-safe pb-safe',
        'transition ease-in-out data-[state=open]:duration-300 data-[state=closed]:duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
        className,
      )}
      {...props}
    >
      {children}
    </SheetPrimitive.Content>
  </SheetPrimitive.Portal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

export const SheetTitle = SheetPrimitive.Title;
export const SheetDescription = SheetPrimitive.Description;
